import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "../component/Pagination";
import { toast } from "react-toastify";

import {
  fetchLabels,
  createLabel,
  updateLabel,
  deleteLabel,
  toggleLabelStatus
} from "../../../slice/label/labelSlice";

export default function Label() {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { labels = [], loading, pagination = {} } = useSelector(
    (state) => state.label
  );

  /* ================= LOCAL STATE ================= */
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [labelName, setLabelName] = useState("");
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState("");

  /* ================= FETCH ================= */
  useEffect(() => {
    dispatch(fetchLabels({ page, limit }));
  }, [dispatch, page]);

  /* ================= ADD ================= */
  const handleCreate = () => {
    if (!labelName.trim()) {
      setError("Label name is required");
      return;
    }

    dispatch(createLabel({ name: labelName.trim() }))
      .unwrap()
      .then(() => {
        toast.success("Label added successfully");
        closeAllModals();
        dispatch(fetchLabels({ page, limit }));
      });
  };

  /* ================= UPDATE ================= */
  const handleUpdate = () => {
    if (!labelName.trim() || !selectedLabel) {
      setError("Label name is required");
      return;
    }

    dispatch(updateLabel({ id: selectedLabel._id, name: labelName.trim() }))
      .unwrap()
      .then(() => {
        toast.success("Label updated successfully");
        closeAllModals();
        dispatch(fetchLabels({ page, limit }));
      });
  };

  /* ================= DELETE ================= */
  const confirmDelete = () => {
    dispatch(deleteLabel(deleteId))
      .unwrap()
      .then(() => {
        toast.success("Label deleted successfully");
        closeAllModals();
        dispatch(fetchLabels({ page, limit }));
      });
  };

  /* ================= HELPERS ================= */
  const closeAllModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedLabel(null);
    setDeleteId(null);
    setLabelName("");
    setError("");
  };

  return (
    <div className="row">
      <div className="card mt-3">

        {/* HEADER */}
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title">Label List</h4>
          <button
            className="btn cusBTNPop btn-sm"
            onClick={() => {
              setLabelName("");
              setSelectedLabel(null);
              setShowAddModal(true);
            }}
          >
            + Add Label
          </button>
        </div>

        {/* TABLE */}
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th>SI</th>
                <th>Label Name</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center">Loading...</td>
                </tr>
              ) : labels.length ? (
                labels.map((label, index) => (
                  <tr key={label._id}>
                    <td>{index + 1}</td>
                    <td>{label.name}</td>

                    {/* STATUS */}
                    <td>
                      <div
                        className={`status-toggle ${label.status ? "enabled" : "disabled"}`}
                       onClick={() => {
  dispatch(toggleLabelStatus(label._id))
    .unwrap()
    .then((res) => {
      toast.success(
        res.status ? "Label Enabled" : "Label Disabled"
      );
    })
    .catch(() => {
      toast.error("Failed to update status");
    });
}}

                      >
                        <span className="toggle-knob"></span>
                        <span className="toggle-text">
                          {label.status ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </td>

                    {/* ACTION */}
                    <td className="d-flex gap-2">
                      <button
                        className="btn btn-soft-primary btn-sm"
                        onClick={() => {
                          setSelectedLabel(label);
                          setLabelName(label.name);
                          setShowEditModal(true);
                        }}
                      >
                        <iconify-icon icon="solar:pen-2-broken" />
                      </button>

                      <button
                        className="btn btn-soft-danger btn-sm"
                        onClick={() => {
                          setDeleteId(label._id);
                          setShowDeleteModal(true);
                        }}
                      >
                        <iconify-icon icon="solar:trash-bin-minimalistic-2-broken" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center text-muted">
                    No labels found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="card-footer border-top">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>

        {/* ADD MODAL */}
        {showAddModal && (
          <div className="modal-overlay" onClick={closeAllModals}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Add Label</h5>
              </div>
              <div className="modal-body">
                <input
                  className={`form-control ${error ? "is-invalid" : ""}`}
                  placeholder="Enter Label Name"
                  value={labelName}
                  onChange={(e) => {
                    setLabelName(e.target.value);
                    setError("");
                  }}
                />
                {error && (
                  <div className="invalid-feedback d-block">
                    {error}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn cuscancel" onClick={closeAllModals}>
                  Cancel
                </button>
                <button className="btn cusBTNPop" onClick={handleCreate}>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* EDIT MODAL */}
        {showEditModal && (
          <div className="modal-overlay" onClick={closeAllModals}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Edit Label</h5>
              </div>
              <div className="modal-body">
                <input
                  className={`form-control ${error ? "is-invalid" : ""}`}
                  placeholder="Enter Label Name"
                  value={labelName}
                  onChange={(e) => {
                    setLabelName(e.target.value);
                    setError("");
                  }}
                />
                {error && (
                  <div className="invalid-feedback d-block">
                    {error}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn cuscancel" onClick={closeAllModals}>
                  Cancel
                </button>
                <button className="btn cusBTNPop" onClick={handleUpdate}>
                  Update
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE MODAL */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={closeAllModals}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-body text-center">
                Are you sure you want to delete this label?
              </div>
              <div className="modal-footer">
                <button className="btn cusClose" onClick={closeAllModals}>
                  Cancel
                </button>
                <button className="btn cuscancel" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
